import React, { useState } from 'react';
import { Loader2, Stethoscope, ChevronDown, ChevronUp, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI } from '../services/api';

const SEV = { critical: 'bg-red-50 text-red-700 border-red-200', major: 'bg-amber-50 text-amber-700 border-amber-200', minor: 'bg-gray-50 text-gray-600 border-gray-200' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '');
const confirmFor = (code, path) => ({ duplicate: 'Cette fiche (la plus récente) sera dépubliée et redirigée (301) vers le doublon le plus ancien, qui est conservé. Continuer ?', thin_content: path.startsWith('/produit/') ? 'La description actuelle du produit sera remplacée par un texte rédigé par IA (puis traduite). Continuer ?' : null }[code] || null);

// Bouton « Corriger » d'un signal : applique la correction côté serveur (IA, republication, redirection) puis rafraîchit le diagnostic
const FixButton = ({ path, finding, onFixed, disabled }) => {
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState(null); // null = pas de saisie ouverte
  if (!finding.action) return null;
  const run = async (t) => {
    const msg = confirmFor(finding.code, path);
    if (msg && !window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await adminAPI.seoIndexationFix(path, finding.code, t || null);
      toast.success(r.message);
      setTarget(null);
      onFixed(r.diagnosis);
    } catch (e) { toast.error(e.response?.data?.detail || 'Correction impossible'); } finally { setBusy(false); }
  };
  const openRedirect = async () => {
    setBusy(true);
    try { const r = await adminAPI.seoIndexationFixPropose(path); setTarget(r.target || ''); } catch { setTarget(''); } finally { setBusy(false); }
  };
  if (finding.code === 'not_found' && target !== null) {
    return (
      <form className="flex items-center gap-1 mt-1" onSubmit={(e) => { e.preventDefault(); run(target); }} data-testid={`seo-fix-redirect-form-${path}`}>
        <input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="/produit/… ou /pieces/…" className="flex-1 min-w-0 border border-gray-300 rounded px-1.5 py-0.5 text-[11px]" data-testid={`seo-fix-redirect-target-${path}`} />
        <button type="submit" disabled={busy || !target.startsWith('/')} className="text-[11px] bg-[#1e3a5f] text-white rounded px-2 py-0.5 disabled:opacity-50" data-testid={`seo-fix-redirect-submit-${path}`}>{busy ? '…' : 'Rediriger'}</button>
        <button type="button" onClick={() => setTarget(null)} className="text-[11px] text-gray-400 hover:text-gray-600">✕</button>
      </form>
    );
  }
  return (
    <button type="button" onClick={() => (finding.code === 'not_found' ? openRedirect() : run())} disabled={busy || disabled} className="inline-flex items-center gap-1 text-[11px] font-medium text-white bg-orange-500 hover:bg-orange-600 rounded px-2 py-0.5 mt-1 disabled:opacity-50" data-testid={`seo-fix-${finding.code}-${path}`}>
      {busy ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />} {finding.action}
    </button>
  );
};

// Cellule « Diagnostic » du suivi d'indexation : cause principale + correction (en un clic quand c'est possible), détail dépliable
export const SeoDiagnosisCell = ({ diag, path, busy, onRun, onFixed, disabled }) => {
  const [open, setOpen] = useState(false);
  const pr = diag?.primary;
  if (!pr) {
    return (
      <button type="button" onClick={onRun} disabled={disabled} className="inline-flex items-center gap-1 text-xs text-[#1e3a5f] hover:underline disabled:opacity-50" data-testid={`seo-indexation-diagnose-${path}`}>
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Stethoscope size={12} />} {diag?.error ? 'Réessayer' : 'Analyser'}
      </button>
    );
  }
  const others = (diag.findings || []).slice(1);
  return (
    <div className="max-w-[280px]" data-testid={`seo-indexation-diagnosis-${path}`}>
      <span className={`inline-block text-[11px] px-1.5 py-0.5 rounded border font-medium ${SEV[pr.severity]}`} data-testid={`seo-indexation-diagnosis-primary-${path}`}>{pr.title}</span>
      <p className="text-[11px] text-gray-700 mt-1 leading-snug">{pr.fix}</p>
      <FixButton path={path} finding={pr} onFixed={onFixed} disabled={disabled} />
      {diag.last_fix && <p className="text-[10px] text-green-700 mt-1" data-testid={`seo-indexation-lastfix-${path}`}>✔ {diag.last_fix.result} ({fmt(diag.last_fix.at)})</p>}
      <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-400">
        {(pr.detail || others.length > 0) && <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-0.5 hover:text-gray-600" data-testid={`seo-indexation-diagnosis-toggle-${path}`}>{open ? <ChevronUp size={11} /> : <ChevronDown size={11} />} {others.length ? `${others.length} autre(s) signal(aux)` : 'détail'}</button>}
        <button type="button" onClick={onRun} disabled={disabled} className="inline-flex items-center gap-0.5 hover:text-gray-600 disabled:opacity-50" data-testid={`seo-indexation-diagnose-${path}`}>{busy ? <Loader2 size={11} className="animate-spin" /> : <Stethoscope size={11} />} relancer</button>
        <span>{fmt(diag.diagnosed_at)}</span>
      </div>
      {open && (
        <ul className="mt-1 space-y-1 text-[11px] text-gray-600 border-l-2 border-gray-200 pl-2" data-testid={`seo-indexation-diagnosis-details-${path}`}>
          {pr.detail && <li className="text-gray-500 italic">{pr.detail}</li>}
          {others.map((f) => <li key={f.code}><span className={`px-1 rounded border ${SEV[f.severity]}`}>{f.title}</span> {f.detail && <span className="text-gray-400">— {f.detail}</span>}<div className="text-gray-700">{f.fix}</div><FixButton path={path} finding={f} onFixed={onFixed} disabled={disabled} /></li>)}
        </ul>
      )}
    </div>
  );
};

export default SeoDiagnosisCell;
